import { HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OcrService } from '@core/services';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { Attachment } from './email-oauth.service';

// Define our own interface since we can't use RunThroughPngFile
export interface EmailOcrPngFile {
  id: number;
  filename: string;
  location: string;
  size: number;
  type: string;
  parentId?: number; // To link back to the original PDF
}

export interface EmailOcrResult {
  originalAttachment: Attachment;
  uploadedFile: EmailPdfUploadResult; // Kompletna struktura sa originalPdf i generatedPngs
  pngFiles: EmailOcrPngFile[];
  ocrData: any;
}

export interface EmailPdfUploadResult {
  // PDF upload response contains both PDF and generated PNG files
  originalPdf: {
    id: number;
    filename: string;
    location: string;
    size: number;
    type: string;
  };
  generatedPngs: EmailOcrPngFile[];
}

@Injectable({
  providedIn: 'root',
})
export class EmailOcrService {
  constructor(private ocrService: OcrService) {}

  /**
   * Process email attachment through complete OCR pipeline
   * Follows the same flow as add-application: PDF → PNG → OCR
   */
  processEmailAttachment(attachment: Attachment, tenantId: number): Observable<EmailOcrResult> {
    // Validate attachment
    if (!this.canProcessWithOcr(attachment)) {
      return throwError(
        () => new Error(`Attachment ${attachment.Filename} cannot be processed with OCR`)
      );
    }

    // Step 1: Upload PDF and convert to PNG
    return this.uploadPdfForOcr(attachment, tenantId).pipe(
      switchMap(uploadResult =>
        // Step 2: Process PNG files with OCR
        this.processOcrOnPngFiles(uploadResult.generatedPngs).pipe(
          map(ocrData => ({
            originalAttachment: attachment,
            uploadedFile: uploadResult, // Kompletna struktura sa originalPdf i generatedPngs
            pngFiles: uploadResult.generatedPngs,
            ocrData,
          }))
        )
      ),
      catchError(error => {
        console.error('Error processing email attachment:', error);
        return throwError(() => new Error(`Failed to process attachment: ${error.message}`));
      })
    );
  }

  /**
   * Process multiple attachments in parallel
   */
  processMultipleAttachments(
    attachments: Attachment[],
    tenantId: number
  ): Observable<EmailOcrResult[]> {
    const processableAttachments = attachments.filter(att => this.canProcessWithOcr(att));

    if (processableAttachments.length === 0) {
      return of([]);
    }

    const processingTasks = processableAttachments.map(attachment =>
      this.processEmailAttachment(attachment, tenantId)
    );

    return forkJoin(processingTasks);
  }

  /**
   * Upload PDF from email and convert to PNG files
   * Uses the same endpoint as add-application OCR flow
   */
  private uploadPdfForOcr(
    attachment: Attachment,
    tenantId: number
  ): Observable<EmailPdfUploadResult> {
    try {
      // Convert base64 to blob
      const blob = this.base64ToBlob(attachment.Data, attachment.ContentType);

      // Create FormData for upload - same format as add-application
      const formData = new FormData();
      formData.append('file', blob, attachment.Filename);

      // Use the same OCR service endpoint as add-application
      // Note: uploadPdfForOcr uses observe: 'events', so we need to filter for the final response
      return this.ocrService.uploadPdfForOcr(formData, 'application', tenantId, 'ocr').pipe(
        // Filter for the final response event (HttpEventType.Response = 4)
        filter((event: any) => event.type === HttpEventType.Response),
        // Extract the body from the HttpResponse
        map((event: any) => {
          const response = event.body;

          // Response is array of files: [PDF, PNG1, PNG2, ...]
          const files = Array.isArray(response) ? response : [response];

          // Find PDF file (original)
          const pdfFile = files.find(file => {
            const filename = file?.filename || file?.name || '';
            return filename.toLowerCase().endsWith('.pdf');
          });

          // Find PNG files (generated)
          // Handle cases where type might not be a string
          const pngFiles = files.filter(file => {
            const filename = (file?.filename || file?.name || '').toLowerCase();
            const fileType = file?.type || '';
            const isPngByFilename = filename.endsWith('.png');
            const isPngByType =
              typeof fileType === 'string' && fileType.toLowerCase().includes('image');
            return isPngByFilename || isPngByType;
          });

          if (!pdfFile) {
            throw new Error('PDF file not found in upload response');
          }

          return {
            originalPdf: {
              id: pdfFile.id,
              filename: pdfFile.filename,
              location: pdfFile.location,
              size: pdfFile.size,
              type: pdfFile.type,
            },
            generatedPngs: pngFiles.map(png => ({
              id: png.id,
              filename: png.filename,
              location: png.location,
              size: png.size,
              type: png.type,
            })),
          };
        }),
        catchError(error => {
          console.error('Error uploading PDF for OCR:', error);
          return throwError(() => new Error(`PDF upload failed: ${error.message}`));
        })
      );
    } catch (error) {
      return throwError(() => new Error(`Invalid PDF attachment data: ${error.message}`));
    }
  }

  /**
   * Run OCR processing on PNG files (generated from PDF)
   */
  private processOcrOnPngFiles(pngFiles: EmailOcrPngFile[]): Observable<any> {
    if (!pngFiles || pngFiles.length === 0) {
      return throwError(() => new Error('No PNG files available for OCR processing'));
    }

    // Extract IDs from PNG files for OCR processing
    const pngIds = pngFiles.map(png => png.id);

    return this.ocrService.processOcr(pngIds).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error in PNG OCR processing:', error);
        return throwError(() => new Error(`OCR processing failed: ${error.message}`));
      })
    );
  }

  /**
   * Convert base64 string to Blob
   */
  private base64ToBlob(base64Data: string, contentType: string): Blob {
    if (!base64Data) {
      throw new Error('Base64 data is empty');
    }

    // Remove data URL prefix if present (e.g., "data:application/pdf;base64,...")
    let base64 = base64Data.trim();
    if (base64.includes(',')) {
      // If it contains a comma, it's likely a data URL - extract the base64 part
      base64 = base64.split(',')[1];
    } else if (base64.startsWith('data:')) {
      // Remove data URL prefix if present
      base64 = base64.replace(/^data:[^;]+;base64,/, '');
    }

    // Remove any whitespace (newlines, spaces, etc.)
    base64 = base64.replace(/\s/g, '');

    try {
      // Decode base64
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType || 'application/pdf' });

      return blob;
    } catch (error: any) {
      console.error('Base64 conversion error:', {
        error: error.message,
        dataLength: base64Data.length,
        cleanedLength: base64.length,
        dataPreview: base64Data.substring(0, 100),
      });
      throw new Error(`Invalid base64 data: ${error.message}`);
    }
  }

  /**
   * Check if attachment can be processed with OCR
   */
  canProcessWithOcr(attachment: Attachment): boolean {
    if (!attachment || !attachment.Filename) {
      return false;
    }

    const filename = attachment.Filename.toLowerCase();
    const supportedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.tiff', '.tif'];

    return supportedExtensions.some(ext => filename.endsWith(ext));
  }

  /**
   * Check if attachment is a PDF
   */
  isPdfAttachment(attachment: Attachment): boolean {
    return attachment?.Filename?.toLowerCase().endsWith('.pdf') || false;
  }

  /**
   * Get attachment file extension
   */
  getAttachmentExtension(attachment: Attachment): string {
    if (!attachment?.Filename) return '';

    const parts = attachment.Filename.split('.');
    return parts.length > 1 ? `.${parts[parts.length - 1].toLowerCase()}` : '';
  }

  /**
   * Format attachment size for display
   */
  formatAttachmentSize(sizeInBytes: number): string {
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${Math.round(sizeInBytes / 1024)} KB`;
    return `${Math.round(sizeInBytes / (1024 * 1024))} MB`;
  }
}
